import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, TrendingUp, TrendingDown, DollarSign, Calendar, Users } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import FormInput from '../FormInput';
import Pagination from '../Pagination';
import api from '../api';

const CaisseTab = () => {
  const [caisses, setCaisses] = useState([]);
  const [anneesAcademiques, setAnneesAcademiques] = useState([]);
  const [statsGlobales, setStatsGlobales] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedCaisse, setSelectedCaisse] = useState(null);

  const [formData, setFormData] = useState({
    solde: '',
    annee_academique: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [caisseResponse, anneesResponse] = await Promise.all([
        api.get('/finance/gestion-caisses/'),
        api.get('/etablissement/annees-academiques/')
      ]);
      
      setCaisses(caisseResponse.data.caisses);
      setStatsGlobales(caisseResponse.data.statistiques_globales);
      setAnneesAcademiques(anneesResponse.data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement des caisses');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'add') {
        await api.post('/finance/caisses/', formData);
        toast.success('Caisse créée avec succès');
      } else {
        await api.put(`/finance/caisses/${selectedCaisse.id}/`, formData);
        toast.success('Caisse modifiée avec succès');
      }

      fetchData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde de la caisse');
    }
  };

  const handleEdit = (caisse) => {
    setSelectedCaisse(caisse);
    setFormData({
      solde: caisse.solde,
      annee_academique: caisse.annee_academique.id
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette caisse ?')) {
      try {
        await api.delete(`/finance/caisses/${id}/`);
        fetchData();
        toast.success('Caisse supprimée avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression de la caisse');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      solde: '',
      annee_academique: ''
    });
    setSelectedCaisse(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalMode('add');
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  // Préparer les données pour les graphiques
  const pieData = caisses.map(caisse => ({
    name: caisse.annee_academique.libelle,
    value: caisse.solde,
    color: caisse.annee_academique.status ? '#10B981' : '#6B7280'
  }));

  const barData = caisses.map(caisse => ({
    name: caisse.annee_academique.libelle.substring(0, 10),
    entrees: caisse.statistiques.total_entrees,
    sorties: caisse.statistiques.total_sorties,
    solde: caisse.solde
  }));

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  // Filtrage des données
  const filteredCaisses = caisses.filter(caisse =>
    caisse.annee_academique.libelle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCaisses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCaisses.length / itemsPerPage);

  const anneeOptions = anneesAcademiques.map(annee => ({
    value: annee.id,
    label: `${annee.libelle} ${annee.status ? '(Active)' : ''}`
  }));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 mr-4">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Solde Total</h3>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statsGlobales.solde_total)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 mr-4">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Nombre de Caisses</h3>
              <p className="text-2xl font-bold text-green-600">{statsGlobales.nombre_caisses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 mr-4">
              <TrendingUp className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Caisses Actives</h3>
              <p className="text-2xl font-bold text-yellow-600">{statsGlobales.caisses_actives}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 mr-4">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Total Transactions</h3>
              <p className="text-2xl font-bold text-purple-600">{statsGlobales.transactions_total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition des soldes */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition des Soldes par Année</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({name, value}) => `${name}: ${formatCurrency(value)}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Entrées vs Sorties */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Entrées vs Sorties par Caisse</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="entrees" fill="#10B981" name="Entrées" />
              <Bar dataKey="sorties" fill="#EF4444" name="Sorties" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* En-tête et recherche */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative">
          <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une caisse..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
          />
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle Caisse
        </button>
      </div>

      {/* Tableau des caisses */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Année Académique
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Solde
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entrées
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sorties
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transactions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Aucune caisse trouvée
                  </td>
                </tr>
              ) : (
                currentItems.map((caisse) => (
                  <tr key={caisse.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {caisse.annee_academique.libelle}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`font-semibold ${caisse.solde >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(caisse.solde)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {formatCurrency(caisse.statistiques.total_entrees)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      {formatCurrency(caisse.statistiques.total_sorties)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {caisse.statistiques.nombre_transactions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        caisse.annee_academique.status 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {caisse.annee_academique.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(caisse)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(caisse.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Modal d'ajout/édition */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'add' ? 'Nouvelle Caisse' : 'Modifier la Caisse'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Solde initial (FCFA)"
            type="number"
            value={formData.solde}
            onChange={(e) => handleInputChange('solde', e.target.value)}
            min="0"
            required
          />

          <FormInput
            label="Année Académique"
            type="react-select"
            options={anneeOptions}
            value={formData.annee_academique}
            onChange={(value) => handleInputChange('annee_academique', value)}
            required
          />

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
            >
              {modalMode === 'add' ? 'Créer' : 'Modifier'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CaisseTab; 